import { useEffect, useRef } from "react";

// Momentum smooth scrolling via Lenis + a custom blended cursor.
export default function SmoothScroll({ children }) {
  const dot = useRef(null);
  const ring = useRef(null);

  useEffect(() => {
    let lenis;
    let raf;
    let mounted = true;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    (async () => {
      if (reduce) return;
      const Lenis = (await import("lenis")).default;
      lenis = new Lenis({
        duration: 1.15,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
      });
      const loop = (time) => {
        if (!mounted) return;
        lenis.raf(time);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
      window.__lenis = lenis;
    })();

    return () => {
      mounted = false;
      if (raf) cancelAnimationFrame(raf);
      if (lenis) lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  // Custom cursor
  useEffect(() => {
    if (window.matchMedia("(pointer: coarse)").matches) return;
    let rx = 0, ry = 0, dx = 0, dy = 0;
    let raf;
    const move = (e) => {
      dx = e.clientX;
      dy = e.clientY;
      if (dot.current) {
        dot.current.style.left = dx + "px";
        dot.current.style.top = dy + "px";
      }
    };
    const loop = () => {
      rx += (dx - rx) * 0.18;
      ry += (dy - ry) * 0.18;
      if (ring.current) {
        ring.current.style.left = rx + "px";
        ring.current.style.top = ry + "px";
      }
      raf = requestAnimationFrame(loop);
    };
    const over = (e) => {
      if (e.target.closest("a,button,[data-cursor='hover'],input,textarea,select"))
        document.body.classList.add("cursor-hover");
    };
    const out = (e) => {
      if (e.target.closest("a,button,[data-cursor='hover'],input,textarea,select"))
        document.body.classList.remove("cursor-hover");
    };
    window.addEventListener("mousemove", move);
    document.addEventListener("mouseover", over);
    document.addEventListener("mouseout", out);
    raf = requestAnimationFrame(loop);
    return () => {
      window.removeEventListener("mousemove", move);
      document.removeEventListener("mouseover", over);
      document.removeEventListener("mouseout", out);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <>
      <div ref={ring} className="cursor-ring" aria-hidden />
      <div ref={dot} className="cursor-dot" aria-hidden />
      {children}
    </>
  );
}

export function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -10 });
  else el.scrollIntoView({ behavior: "smooth" });
}
